import React, { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashBoardProfile from "./components/DashBoardProfile";
import Progress from "./components/Progress";
import Courses from "./components/Courses";
import Activity from "./components/Activity";
import Upcoming from "./components/Upcoming";
import Calendar from "./components/Calendar";
import { authService } from "../../../utils/authService";

interface SkeletonProps {
  width: string;
  height: string;
  className?: string; 
}

// Create context for API loading state
interface ApiLoadingContextType {
  isCoursesApiLoaded: boolean;
  coursesData: any;
  coursesError: any;
  studentId: string;
  courseId: string;
  batchId: string;
}

const ApiLoadingContext = createContext<ApiLoadingContextType>({
  isCoursesApiLoaded: false,
  coursesData: null,
  coursesError: null,
  studentId: '',
  courseId: '',
  batchId: ''
});

export const useApiLoading = () => useContext(ApiLoadingContext);

const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get student data from navigation state or sessionStorage (for new tab)
  const [studentId, setStudentId] = useState<string>('');
  const [courseId, setCourseId] = useState<string>('');
  const [batchId, setBatchId] = useState<string>('');

  // Load student data on component mount
  useEffect(() => {
    // First try to get from navigation state
    if (location.state?.studentId) {
      setStudentId(location.state.studentId);
      setCourseId(location.state.courseId || '');
      setBatchId(location.state.batchId || '');
    } else {
      // If no navigation state, try to get from sessionStorage (for new tab or refresh)
      const studentData = sessionStorage.getItem('currentStudentData');
      if (studentData) {
        try {
          const data = JSON.parse(studentData);
          setStudentId(data.studentId || '');
          setCourseId(data.courseId || '');
          setBatchId(data.batchId || '');
        } catch (error) {
          console.error('Error parsing student dashboard data:', error);
          sessionStorage.removeItem('currentStudentData');
          // navigate('/coordinator/overall-progress-report');
        }
      } else {
        // If no data found, redirect immediately
        // navigate('/coordinator/overall-progress-report');
      }
    }
  }, [location.state, navigate]);
  
  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);


  const [isCoursesLoaded, setIsCoursesLoaded] = useState(false);
  
  // Set student data in session storage for API calls
  useEffect(() => {
    if (studentId) {
      sessionStorage.setItem('StudentId', studentId);
    }
    if (courseId) {
      sessionStorage.setItem('CourseId', courseId);
    }
    if (batchId) {
      sessionStorage.setItem('BatchId', batchId);
    }
  }, [studentId, courseId, batchId]);

  // State for courses data
  const [coursesData, setCoursesData] = useState<any>(null);
  const [coursesError, setCoursesError] = useState<any>(null);
  // Fetch courses data
  useEffect(() => {
    const fetchCourses = async () => {
      if (!studentId) return;
      
      setCoursesError(null);
      
      try {
        const response = await authService.get(`coordinator/studentdashboard/mycourses/${studentId}`);
        setCoursesData(response.data);
      } catch (error) {
        setCoursesError(error);
        console.error('Error fetching courses:', error);
      }
    };

    fetchCourses();
  }, [studentId]);

  // Only set courses as loaded when the API response is received
  useEffect(() => {
    if (coursesData || coursesError) {
      setIsCoursesLoaded(true);
    }
  }, [coursesData, coursesError]);

  const Skeleton: React.FC<SkeletonProps> = ({ width, height, className = "" }) => (
    <div
      className={`skeleton ${className}`}
      style={{
        width,
        height,
        backgroundColor: "#e1e1e1",
        borderRadius: "4px",
        margin: "10px 0",
      }}
    ></div>
  );

  // Provide context value
  const contextValue: ApiLoadingContextType = {
    isCoursesApiLoaded: isCoursesLoaded,
    coursesData,
    coursesError,
    studentId,
    courseId,
    batchId
  };


  return (
    <ApiLoadingContext.Provider value={contextValue}>
      <div style={{ backgroundColor: "#F3EDED", minHeight: "100vh" }}>
        <div className="" style={{ backgroundColor: "#F3EDED" }}>
          <div className="container-fluid">
            <div className="row">
              <div
                className="col me-3 mt-3 rounded-3 text-white"
                style={{ backgroundColor: "#111111" }}
              >
                <div className="row me-2 mt-2 mb-2">
                  <div className="col-lg-7 col-md-6 col-xl-8 col-xxl-7">
                    {isCoursesLoaded ? <DashBoardProfile /> : <Skeleton width="100%" height="250px" />}
                  </div>
                  <div className="col-lg-5 col-md-6 col-xl-4 col-xxl-5">
                    {isCoursesLoaded ? <Progress /> : <Skeleton width="100%" height="200px" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-2 mb-2" style={{ marginRight: "0px" }}>
              <div className="col bg-white rounded-3 px-4">
                <Courses />
              </div>
            </div>
            <div className="row mt-2 mb-2" style={{ marginRight: "0px" }}>
              <div className="col-sm-12 col-md-12 col-lg-5 p-0">
                {isCoursesLoaded ? <Activity /> : <Skeleton width="100%" height="300px" />}
              </div>
              <div className="col-sm-12 col-md-8 col-lg-4 p-0">
                {isCoursesLoaded ? <Upcoming /> : <Skeleton width="100%" height="300px" />}
              </div>
              <div className="col-sm-12 col-md-4 col-lg-3 p-0">
                {isCoursesLoaded ? <Calendar /> : <Skeleton width="100%" height="300px" />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ApiLoadingContext.Provider>
  );
};

export default StudentDashboard;
